<template>
  <v-row justify="space-around" class="flex-column flex-sm-row ga-3">
    <!-- Скелетон для загрузки -->
    <template v-if="isLoading">
      <v-card flat class="data-card">
        <v-skeleton-loader type="card" />
      </v-card>
      <v-card elevation="10" class="status-card">
        <v-skeleton-loader type="card" />
      </v-card>
    </template>

    <!-- Ошибка -->
    <template v-else-if="hasError">
      <v-col cols="12">
        <v-alert
          type="error"
          variant="tonal"
          closable
          @click:close="hasError = false"
        >
          {{ errorMessage || "Ошибка загрузки данных" }}
        </v-alert>
      </v-col>
    </template>

    <!-- Основной контент -->
    <template v-else>
      <v-card flat class="data-card">
        <v-card-text>
          <div class="form-row">
            <div class="form-label">Фамилия</div>
            <v-text-field
              v-model="student.lastname"
              hide-details
              variant="outlined"
              density="compact"
              @input="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Имя</div>
            <v-text-field
              v-model="student.firstname"
              hide-details
              variant="outlined"
              density="compact"
              @input="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Отчество</div>
            <v-text-field
              v-model="student.patronymic"
              hide-details
              variant="outlined"
              density="compact"
              @input="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Дата рождения</div>
            <v-text-field
              v-model="student.birthday"
              type="date"
              hide-details
              variant="outlined"
              density="compact"
              @update:modelValue="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Пол</div>
            <v-radio-group
              hide-details
              inline
              v-model="student.gender"
              @update:modelValue="onStudentUpdate"
            >
              <v-radio label="мужской" :value="1" />
              <v-radio label="женский" :value="0" />
            </v-radio-group>
          </div>

          <div class="form-row">
            <div class="form-label">Гражданство</div>
            <v-autocomplete
              hide-details
              :items="countries"
              v-model="student.country_type"
              item-title="country"
              item-value="key"
              variant="outlined"
              density="compact"
              @update:modelValue="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Тип гражданства</div>
            <v-autocomplete
              hide-details
              :items="citizenshipTypes"
              v-model="student.c_type"
              item-title="name"
              item-value="code"
              variant="outlined"
              density="compact"
              @update:modelValue="onStudentUpdate"
            />
          </div>

          <div class="form-row">
            <div class="form-label">Год поступления</div>
            <v-text-field
              v-model="student.start_year"
              hide-details
              variant="outlined"
              density="compact"
              type="number"
              placeholder="2022"
              @input="onStudentUpdate"
            />
          </div>
        </v-card-text>
      </v-card>

      <v-card elevation="10" class="status-card">
        <v-card-text>
          <div class="row-info">
            <span class="title-field">Статус: </span>
            <span
              v-if="editField !== 'status'"
              class="editable-field"
              :class="{ editable: $model.isAdmin }"
              @click.stop="$model.isAdmin && openEdit('status')"
            >
              {{ currentStatusName }}
            </span>
            <v-select
              v-else
              ref="statusSelect"
              v-model="student.status"
              :items="studentStatuses"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              @update:modelValue="onEditSave"
            />
          </div>

          <div class="row-info">
            <span class="title-field">Логин: </span>
            <span>{{ student.login || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Email: </span>
            <span>{{ student.email || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Условие обучения: </span>
            <span
              v-if="editField !== 'budget'"
              class="editable-field"
              :class="{ editable: $model.isAdmin }"
              @click.stop="$model.isAdmin && openEdit('budget')"
            >
              {{ currentBudgetName }}
            </span>
            <v-select
              v-else
              ref="budgetSelect"
              v-model="student.isbudget"
              :items="typeBudget"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              @update:modelValue="onEditSave"
            />
          </div>

          <div class="row-info">
            <span class="title-field">Форма обучения: </span>
            <span>{{ student.study || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Специальность: </span>
            <span>{{ student.specialty || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Группа: </span>
            <span>{{ student.group || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Уровень: </span>
            <span>{{ student.level || "—" }}</span>
          </div>

          <div class="row-info">
            <span class="title-field">Стипендии: </span>
            <template v-if="student.grants && student.grants.length">
              <template v-for="(grant, index) in student.grants" :key="index">
                <span>{{ grant }}</span>
                <br v-if="index < student.grants.length - 1" />
              </template>
            </template>
            <span v-else>Не указаны</span>
          </div>

          <div class="chek-info mt-3">
            <v-checkbox
              v-model="student.in_group"
              label="Пополнение"
              hide-details
              @update:modelValue="onStudentUpdate"
            />
          </div>
          <div class="chek-info">
            <v-checkbox
              v-model="student.quota"
              label="Квота"
              hide-details
              @update:modelValue="onStudentUpdate"
            />
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-row>
</template>

<script>
export default {
  name: "StudentProfileCard",

  props: {
    student: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      editField: null,
      isLoading: true,
      hasError: false,
      errorMessage: null,

      // Данные справочников
      countries: [],
      citizenshipTypes: [],
      studentStatuses: [],
      typeBudget: [
        { value: 0, title: "бюджет" },
        { value: 1, title: "договор" },
      ],
    };
  },

  computed: {
    currentStatusName() {
      const status = this.studentStatuses.find(
        (s) => s.value === this.student.status
      );
      return status ? status.title : "Не указан";
    },

    currentBudgetName() {
      const budget = this.typeBudget.find(
        (b) => b.value === this.student.isbudget
      );
      return budget ? budget.title : "Не указано";
    },
  },

  created() {
    this.loadDirectoryData();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    loadDirectoryData() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = null;

      this.axios
        .post(
          `${this.$model.wwwroot}/procollege/core_procollege/helpers/DirectoryLoader/Load`,
          new URLSearchParams({
            directory: JSON.stringify([
              "oksm",
              "citizenshipType",
              "studentStatuses",
            ]),
          })
        )
        .then((res) => {
          const data = res.data;

          if (data.citizenshipType) {
            this.citizenshipTypes = Object.values(data.citizenshipType).map(
              (item) => ({
                code: item.code,
                name: item.name,
              })
            );
          }

          if (data.oksm) {
            this.countries = Object.values(data.oksm).map((item) => ({
              key: item.alfa2,
              country: item.shortname,
            }));
          }

          if (data.studentStatuses) {
            this.studentStatuses = data.studentStatuses.map((name, index) => ({
              title: name,
              value: index,
            }));
          }
        })
        .catch((err) => {
          this.hasError = true;

          if (err.response?.data) {
            this.errorMessage = err.response.data;
          } else if (err.message) {
            this.errorMessage = err.message;
          } else {
            this.errorMessage = "Ошибка загрузки справочников";
          }

          console.error("Ошибка загрузки справочников:", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onStudentUpdate() {
      this.$emit("update:student", this.student);
    },

    openEdit(field) {
      this.closeEdit();
      this.editField = field;
      this.$nextTick(() => {
        document.addEventListener("click", this.handleClickOutside);
      });
    },

    onEditSave() {
      this.closeEdit();
      this.onStudentUpdate();
    },

    closeEdit() {
      this.editField = null;
      document.removeEventListener("click", this.handleClickOutside);
    },

    handleClickOutside(e) {
      let activeSelect = null;
      if (this.editField === "status") activeSelect = this.$refs.statusSelect;
      if (this.editField === "budget") activeSelect = this.$refs.budgetSelect;

      if (activeSelect) {
        const selectElement = activeSelect.$el;
        if (selectElement && !selectElement.contains(e.target)) {
          this.closeEdit();
        }
      }
    },
  },
};
</script>

<style scoped>
.data-card {
  flex: 1;
  max-width: 600px;
}

.status-card {
  flex: 1;
  max-width: 400px;
  padding: 6px;
}

.chek-info ::v-deep(.v-checkbox label) {
  order: -1 !important;
  margin: 0 !important;
}

.chek-info ::v-deep(.v-checkbox .v-selection-control) {
  min-height: auto !important;
  padding: 0 !important;
}

::v-deep(.v-checkbox .v-selection-control__input),
:deep(.v-radio .v-selection-control__input) {
  color: rgba(0, 75, 140, 1) !important;
}

.row-info {
  font-size: 16px;
  margin-bottom: 10px;
}

.title-field {
  font-weight: 600;
  color: rgba(0, 75, 140, 1);
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .status-card {
    order: -1;
    width: 100%;
  }

  .data-card {
    width: 100%;
  }
}

.editable-field {
  padding: 2px 6px;
  border-radius: 4px;
  transition: 0.2s;
}

.editable {
  background-color: rgba(255, 230, 120, 0.486);
  cursor: pointer;
}

.editable:hover {
  background-color: rgba(255, 230, 120, 0.7);
}
</style>